/**
 * <mobile-menu> — the panels inside the mobile menu drawer (snippets/mobile-menu.liquid).
 * [data-menu-open="<panel id>"] slides a category's panel in, [data-menu-back] returns to
 * the main one. Only the active panel is reachable (the others are inert); focus moves to
 * the new panel and back to the row that opened it. Closing the drawer resets to main.
 * [data-menu-search] closes the drawer and opens the site search dialog (<site-search>).
 */
if (!customElements.get('mobile-menu')) {
  class MobileMenu extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('.offcanvas');
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.drawer?.addEventListener('hidden.bs.offcanvas', () => this.show(this.root, { focus: false }), { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    onClick(event) {
      const opener = event.target.closest('[data-menu-open]');
      if (opener) {
        this.opener = opener;
        this.show(opener.dataset.menuOpen);
        return;
      }
      if (event.target.closest('[data-menu-back]')) {
        this.show('main');
        return;
      }
      if (event.target.closest('[data-menu-search]')) this.openSearch();
    }

    /** The first panel: the guided shopping one when it is on (assets/rug-finder.js adds is-guided). */
    get root() {
      return this.classList.contains('is-guided') ? 'guide' : 'main';
    }

    // Escape inside a category panel goes back one level (the drawer closes from main).
    onKeydown(event) {
      if (event.key !== 'Escape' || this.active === 'main' || this.active === 'guide' || !this.active) return;
      event.stopPropagation();
      this.show('main');
    }

    show(name, { focus = true } = {}) {
      const target = this.querySelector(`[data-menu-panel="${name}"]`);
      if (!target) return;
      // Depth: guide → main → a category. Panels above the target slide away, the ones below wait behind.
      const depth = (panelName) => (panelName === 'guide' ? 0 : panelName === 'main' ? 1 : 2);
      const goingBack = name === 'main' || name === 'guide';
      this.querySelectorAll('[data-menu-panel]').forEach((panel) => {
        const active = panel === target;
        panel.classList.toggle('is-active', active);
        panel.classList.toggle('is-behind', !active && depth(panel.dataset.menuPanel) < depth(name));
        panel.inert = !active;
        if (active) panel.querySelector('.mm__body')?.scrollTo(0, 0);
      });
      this.active = name;
      if (!focus) return;
      const focusTarget = goingBack ? this.opener : target.querySelector('[data-menu-back]');
      focusTarget?.focus({ preventScroll: true });
      if (goingBack) this.opener = null;
    }

    /**
     * Opens the search inside the tap itself: phones only raise the keyboard for a focus()
     * made during the user's gesture, so waiting for the drawer's slide-out (hidden.bs.*)
     * opened the dialog without a keyboard. The drawer slides out underneath the dialog.
     */
    openSearch() {
      const offcanvas = this.drawer && window.bootstrap?.Offcanvas.getInstance(this.drawer);
      offcanvas?.hide(); // releases the drawer's focus trap before the dialog takes focus
      document.querySelector('site-search')?.open('menu');
    }
  }

  customElements.define('mobile-menu', MobileMenu);
}
